import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Navbar from "@/sections/Navbar";
import Footer from "@/sections/Footer";
import { Reveal, Eyebrow } from "@/lib/motion";

const VALUES = [
  { no: "01", title: "Outcomes over hype", body: "We don't sell buzzwords. Every agent we build is judged on one thing: does it save you time or make you money?" },
  { no: "02", title: "Built around you", body: "Your AI employees are trained on your business, your tools, and your voice — not a generic template." },
  { no: "03", title: "Human in the loop", body: "Automation should free your team, not replace them. We design systems people actually enjoy working alongside." },
  { no: "04", title: "We stay after launch", body: "AI improves with use. We monitor, refine, and expand your agents as your business grows." },
];

const STATS = [
  { k: "1–2 wks", v: "Typical time to launch" },
  { k: "24/7", v: "Always-on coverage" },
  { k: "10+", v: "Tools we integrate with" },
];

export default function AboutPage() {
  return (
    <div className="bg-brand-cream text-brand-ink font-body antialiased min-h-screen">
      <Navbar />
      <main className="pt-28 md:pt-36">
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-6 md:px-12">
          <Eyebrow>About PeachFlow AI</Eyebrow>
          <h1 className="mt-6 font-display font-normal text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight max-w-4xl">
            We build <em className="italic text-brand-teal">AI employees</em> that give small teams superpowers.
          </h1>
          <p className="mt-8 text-lg md:text-xl leading-relaxed text-brand-ink/75 max-w-2xl">
            PeachFlow AI is a small studio on a mission: help everyday businesses compete with the big players by putting reliable, always-on AI to work — answering customers, chasing leads, and handling the busywork that slows teams down.
          </p>
        </section>

        {/* Stats */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 mt-16 md:mt-24">
          <div className="grid sm:grid-cols-3 gap-6">
            {STATS.map((s, i) => (
              <Reveal key={s.k} delay={i * 0.1}>
                <div className="rounded-3xl border border-brand-peach bg-brand-cream p-8">
                  <div className="font-display text-4xl md:text-5xl text-brand-teal">{s.k}</div>
                  <div className="mt-2 font-mono text-xs uppercase tracking-[0.15em] text-brand-ink/60">{s.v}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Story */}
        <section className="bg-brand-ink text-brand-cream my-16 md:my-24 py-24 md:py-32">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <Eyebrow className="!text-brand-sage">Our story</Eyebrow>
            <div className="mt-6 space-y-6 text-lg leading-relaxed text-brand-cream/80">
              <p>
                We started PeachFlow because we kept seeing the same thing: brilliant small businesses drowning in repetitive work. Missed leads, late follow-ups, inboxes overflowing, and no time left for the work that actually grows the company.
              </p>
              <p>
                Powerful AI existed — but it was locked behind enterprise budgets and complicated tooling. So we made it our job to bridge that gap: to design custom AI employees that are genuinely useful, easy to live with, and priced for real small businesses.
              </p>
              <p>
                Today we build agents for bakeries, clinics, agencies, and trades — quietly handling thousands of conversations and tasks so the humans can do what they do best.
              </p>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="max-w-7xl mx-auto px-6 md:px-12">
          <Eyebrow>What we believe</Eyebrow>
          <h2 className="mt-5 mb-12 md:mb-16 font-display font-normal text-4xl md:text-5xl tracking-tight">
            Principles we build by.
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.no} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-brand-peach p-8">
                  <div className="font-display text-5xl text-brand-apricot mb-4">{v.no}</div>
                  <h3 className="font-display text-2xl mb-3">{v.title}</h3>
                  <p className="text-brand-ink/75 leading-relaxed">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 mt-16 md:mt-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="rounded-3xl bg-brand-sage p-10 md:p-16 flex flex-col md:flex-row md:items-center justify-between gap-8"
          >
            <h2 className="font-display font-normal text-3xl md:text-4xl lg:text-5xl tracking-tight text-brand-ink max-w-xl">
              Ready to hire your first AI employee?
            </h2>
            <Link
              to="/#book"
              data-testid="about-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-brand-ink text-brand-cream pl-7 pr-3 py-3 font-mono text-sm uppercase tracking-[0.15em] hover:bg-brand-teal transition-colors duration-300 shrink-0"
            >
              Book a call
              <span className="w-9 h-9 rounded-full bg-brand-cream/20 grid place-items-center group-hover:rotate-45 transition-transform duration-300">
                <ArrowUpRight size={18} />
              </span>
            </Link>
          </motion.div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
